/**
 * Reine Hilfen der Seite „Hydraulischer Abgleich“ (TH-4, 17.10.2026).
 *
 * Fachstatus, Bereichstitel, Einstellart, Armaturart und Nachkommastellen sind
 * DIESELBEN wie im Bericht HZG-ABG (`Infrastructure/Heizung/Abgleichnachweis.cs`,
 * `Abgleichtexte`): eine Zahl, eine Darstellung (TX-STANDARD S0.5, S1.5, S1.7).
 * Ein Test hält beide Listen gleich.
 */

import type { AbgleichLauf, AbgleichModell, AbgleichVentil } from '../../api';
import { zeitpunkt } from './kaelte';

export { zustandErgebnis, zustandHerkunftNachweis } from './kaeltenachweis';

/** Die Bereiche der Prüfungen in der Reihenfolge von Seite, Bericht und Liste (Abgleichlauf.Bereiche). */
export const BEREICHE: readonly string[] = ['lauf', 'gruppe', 'pumpe'];

/** Der Bereich als Abschnittstitel (Abgleichtexte.Bereich). */
export const BEREICH_TITEL: Record<string, string> = {
  lauf: 'Grundlage des Abgleichs',
  gruppe: 'Abgleich je Auslegungsgruppe',
  pumpe: 'Pumpen',
};

/** Was der Bereich zeigt – ein Satz unter dem Abschnittstitel. */
export const BEREICH_BESCHREIBUNG: Record<string, string> = {
  lauf: 'Steht der Rohrnetzlauf auf dem maßgebenden Heizlastlauf, und trägt jede Heizfläche die raumweise Heizlast (Verfahren B)?',
  gruppe: 'Je Auslegungsgruppe: einstellbar, Förderhöhe nach dem Abgleich und der Pumpenbetriebspunkt ohne Ventil-Mindestdruck (F-TH-13).',
  pumpe: 'Betriebspunkt nach dem Abgleich im Kennfeld, Einstellwert nach Regelungsart und EEI – nur mit Herstellerangabe.',
};

/** Die Einstellart im Klartext (Abgleichtexte.Einstellart). */
export const EINSTELLART: Record<string, string> = {
  voreinstellung: 'Voreinstellung',
  kv_wert: 'kv-Wert',
  offen: 'offen',
  ohne: '—',
};

/** Die Armaturart im Klartext (Abgleichtexte.Armatur). */
export const ARMATUR: Record<string, string> = {
  strangregulierventil: 'Strangregulierventil',
  dp_regler: 'Differenzdruckregler',
  einzel: 'Abgleichventil am Verbraucher',
};

/** Nachkommastellen je Einheit (Abgleichtexte.Nachkomma, Einheitenkatalog S1.7). */
export function nachkomma(einheit: string | null | undefined): number {
  switch (einheit) {
    case 'kPa': return 1;
    case 'Pa': return 0;
    case 'm': case 'm³/h': case '-': return 2;
    case 'kg/h': case 'Stk': case 'Wege': return 0;
    default: return 1;
  }
}

/** Der Einstellwert einer Zeile, wie er auf der Monteurliste steht. */
export function einstellwert(v: AbgleichVentil, format: (x: number, n: number) => string): string {
  switch (v.einstellart) {
    case 'voreinstellung': return v.voreinstellung === null ? '—' : `Stufe ${format(v.voreinstellung, 1)}`;
    case 'kv_wert': return v.kvErforderlich === null ? '—' : `kv ${format(v.kvErforderlich, 3)}`;
    case 'offen': return 'offen';
    default: return v.grund ?? '—';
  }
}

/** Pa als kPa für die Anzeige (gespeichert wird Pa, gerundet nur beim Zeigen – S1.7). */
export function kpa(pa: number | null | undefined): number | null {
  return pa === null || pa === undefined ? null : pa / 1000;
}

/**
 * F72: Eine Gruppe ist eine untere Schranke, wenn einer ihrer Verbraucher keinen gerechneten Weg hat
 * (Abgleichnachweis.UntereSchranke) – dann trägt ihre Förderhöhe das „≥“.
 */
export function untereSchranke(modell: AbgleichModell, gruppe: string): boolean {
  return modell.ventile.some((v) => v.systemgruppe === gruppe && v.wegPa === null);
}

/** Die Gruppe einer Prüfzeile: bei Gruppen der Gegenstand, bei Pumpen der Teil vor „: “ (Abgleichlauf.Gruppentrenner). */
export function gruppeVon(p: { bereich: string; gegenstand: string }): string | null {
  if (p.bereich === 'gruppe') return p.gegenstand;
  if (p.bereich === 'pumpe') {
    const i = p.gegenstand.indexOf(': ');
    return i > 0 ? p.gegenstand.slice(0, i) : null;
  }
  return null;
}

/** Eine Zeile der Gruppenübersicht – gezählt aus den Einstellwertzeilen, nicht gerechnet. */
export interface Gruppenzeile {
  gruppe: string;
  verbraucher: number;
  mitEinstellwert: number;
  nichtEinstellbar: number;
  ohneWeg: number;
  armaturen: number;
  regler: number;
}

export function gruppen(modell: AbgleichModell): Gruppenzeile[] {
  const namen = [...new Set(modell.ventile.map((v) => v.systemgruppe))];
  return namen.map((gruppe) => {
    const v = modell.ventile.filter((x) => x.systemgruppe === gruppe);
    const a = modell.armaturen.filter((x) => x.systemgruppe === gruppe);
    return {
      gruppe,
      verbraucher: v.length,
      mitEinstellwert: v.filter((x) => x.einstellart !== 'ohne').length,
      nichtEinstellbar: v.filter((x) => x.ergebnis === 'nicht_erfuellt').length,
      ohneWeg: v.filter((x) => x.wegPa === null).length,
      armaturen: a.filter((x) => x.art !== 'einzel').length,
      regler: a.filter((x) => x.art === 'dp_regler').length,
    };
  });
}

/** Der Rechenstand (S1.2): „Lauf ‹Kurz-ID› · gerechnet ‹…› · Heizlast-Lauf ‹Nr› · Rohrnetz vom ‹…›“. */
export function rechenstandAbgleich(lauf: AbgleichLauf | null): string {
  if (!lauf) return 'noch nicht gerechnet';
  const teile = [`Lauf ${lauf.id.slice(0, 8)}`, `gerechnet ${zeitpunkt(lauf.beendetAm ?? lauf.gestartetAm)}`];
  if (lauf.heizlastLaufnummer !== null) teile.push(`Heizlast-Lauf ${lauf.heizlastLaufnummer}`);
  if (lauf.rohrnetzAm) teile.push(`Rohrnetz vom ${zeitpunkt(lauf.rohrnetzAm)}`);
  return teile.join(' · ');
}
